import { LinearProgress, Theme, Typography, useTheme } from "@material-ui/core";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";

interface FuelLevelHeaderProps {
  fuelLevel?: string | null;
  fuelLevelUpdateTime?: string | null;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const parseFuelPercent = (
  fuelLevel: string | null | undefined
): number | null => {
  if (!fuelLevel) return null;

  const match = String(fuelLevel).match(/-?\d+(\.\d+)?/);
  if (!match) return null;

  const numeric = Number(match[0]);
  if (!Number.isFinite(numeric)) return null;

  const percent = numeric > 0 && numeric <= 1 ? numeric * 100 : numeric;
  return clamp(percent, 0, 100);
};

const FuelLevelHeader = ({
  fuelLevel,
  fuelLevelUpdateTime,
}: FuelLevelHeaderProps) => {
  const theme = useTheme<Theme>();

  const fuelPercent = useMemo(() => parseFuelPercent(fuelLevel), [fuelLevel]);

  const fuelLevelUpdateToNow = useMemo(() => {
    if (!fuelLevelUpdateTime) return null;
    return formatDistanceToNow(new Date(fuelLevelUpdateTime), {
      addSuffix: true,
    });
  }, [fuelLevelUpdateTime]);

  const rightText =
    typeof fuelPercent === "number"
      ? `${Math.round(fuelPercent)}%`
      : fuelLevel ?? "-";

  return (
    <div
      style={{
        padding: "8px 16px 10px 16px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
          gap: "8px",
        }}
      >
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          Fuel level{fuelLevelUpdateToNow ? ` - ${fuelLevelUpdateToNow}` : ""}
        </Typography>
        <Typography
          variant="caption"
          style={{ fontWeight: 600, color: theme.palette.text.primary }}
        >
          {rightText}
        </Typography>
      </div>

      <LinearProgress
        variant="determinate"
        value={fuelPercent ?? 0}
        style={{ height: "6px", borderRadius: "6px" }}
      />
    </div>
  );
};

export default FuelLevelHeader;
